import { Search, SentimentDissatisfied } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";

import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import "./Products.css";
import ProductCard from "./ProductCard";
import Cart from "./Cart"
import {generateCartItemsFrom} from "./Cart"

// import Checkout from "./Checkout";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 * 
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */


const Products = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const [searchResult, setSearchResult] = useState([])
  const debounceTimeout = useRef(null);
  const [cartItems, setCartitems] = useState([])
  const [completeCartitems, setCompleteCartitems] = useState([])
  const isLoggedIn = localStorage.getItem('token');
  const { enqueueSnackbar } = useSnackbar();
 

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Fetch products data and store it
  /**
   * Make API call to get the products list and store it to display the products
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on all available products
   *
   * API endpoint - "GET /products"
   *
   * Example for successful response from backend:
   * HTTP 200
   * [
   *      {
   *          "name": "iPhone XR",
   *          "category": "Phones",
   *          "cost": 100,
   *          "rating": 4,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "v4sLtEcMpzabRyfx"
   *      },
   *      {
   *          "name": "Basketball",
   *          "category": "Sports",
   *          "cost": 100,
   *          "rating": 5,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "upLK9JbQ4rMhTwt4"
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 500
   * {
   *      "success": false,
   *      "message": "Something went wrong. Check the backend console for more details"
   * }
   */
  // Fetch Produtcs
  const performAPICall = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${config.endpoint}/products`);
      // console.log(response.data);
      setProducts(response.data)
      
    } catch (error) {
      console.error("Error Fetching products", error);
      setError(true)
    }
    setIsLoading(false)
    // finally{
      
    //   // setTimeout(()=>{
    //   // },1000)
    // }
  };

  

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Implement search logic
  /**
   * Definition for search handler
   * This is the function that is called on adding new search keys
   *
   * @param {string} text
   *    Text user types in the search bar. To filter the displayed products based on this text.
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on filtered set of products
   *
   * API endpoint - "GET /products/search?value=<search-query>"
   *
   */
  
  // TODO: CRIO_TASK_MODULE_PRODUCTS - Optimise API calls with debounce search implementation
  const performSearch = async (text) => {
    try {
      setIsLoading(true);
      const searchResponse = await axios.get(
        `${config.endpoint}/products/search?value=${encodeURIComponent(text)}`
      );
      setSearchResult(searchResponse.data); // Ensure this is an array
      setError(false); 
    } catch (error) {
      if (error.response?.status === 404) {
        setSearchResult([]); // Explicitly set empty array
        setError(true);
      } else {
        console.error("Search error", error);
        setError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Definition for debounce handler
   * With debounce, this is the function to be called whenever the user types text in the searchbar field
   *
   * @param {{ target: { value: string } }} event
   *    JS event object emitted from the search input field
   *
   * @param {NodeJS.Timeout} debounceTimeout
   *    Timer id set for the previous debounce call
   *
   */


const debounceSearch = (event) => {
  const value = event.target.value;
  setInputVal(value);

  // Clear previous timeout
  if (debounceTimeout.current) {
    clearTimeout(debounceTimeout.current);
  }

  // Only set new timeout if there's input
  debounceTimeout.current = setTimeout(() => {
    if (value.trim()) {
      performSearch(value);
    } else {
      setSearchResult([]);
      setError(false);
    }
  }, 500);
};

// Fetch carts
async function fetchCart(){
  try {   
    const token = localStorage.getItem('token');
    if(!token){
      console.log("No token found, user might not be logged In");
      // throw new Error("No Authorisation Tokan Found")
      return []
    }

    const resData = await axios.get(`${config.endpoint}/cart`,{
      headers:{
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });
    
    return resData.data;  

  } catch (error) {
    console.error("Something went wrong. Check the backend console for more details", error)
    return []
  } 
}

// handle quantity changes - make an API call just like handleAddToCart
const handleQuantity = async (productId, newQty) => {
  // Ensure user is logged in (mirrors handleAddToCart)
  if (!isLoggedIn) {
    enqueueSnackbar("Login to update the Cart", { variant: "warning" });
    return;
  }
  try {
    const token = localStorage.getItem("token");
    const response = await axios.post(
      `${config.endpoint}/cart`,
      { productId, qty: newQty },
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    // response.data is the updated raw cart array [{ productId, qty }, ...]
    setCartitems(response.data);

    // regenerate complete cart items for Cart component
    const merged = generateCartItemsFrom(response.data, products);
    setCompleteCartitems(merged);

    if (newQty === 0) {
      enqueueSnackbar(`Item removed from cart`, { variant: "success" });
    } else if (newQty > 1) {
      enqueueSnackbar(`Item updated to ${newQty}`, { variant: "success" });
    } else {
      enqueueSnackbar(`Item added to cart`, { variant: "success" });
    }
  } catch (error) {
    console.error("Error updating cart quantity", error);
    enqueueSnackbar("Failed to update cart", { variant: "error" });
  }
};


// add to cart handler
// const handleCart = (product) => {
//   setCart(prevCart => {
//     const existing = prevCart.find(item => item._id === product._id);

//     if (existing) {
//       // increase quantity
//       return prevCart.map(item =>
//         item._id === product._id
//           ? { ...item, qty: item.qty + 1 }
//           : item
//       );
//     }

//     // add new product with qty = 1
//     return [...prevCart, { ...product, qty: 1 }];
//   });
// };

// console.log(isLoggedIn);

   useEffect(() => {
     
     performAPICall();

     const getCart = async ()=>{
       try {
        const cartData  = await fetchCart();
        // console.log("Raw Cart Data:", cartData);
          setCartitems(cartData)
         
        } catch (error) {
          console.error("Cart item not loaded", error)
        }
      }
      
      getCart()

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  useEffect(()=>{
    if(products.length > 0 && cartItems.length > 0){
      const mergeCartProducts = generateCartItemsFrom(cartItems, products);
      setCompleteCartitems(mergeCartProducts)
      // console.log("completeCartitems",mergeCartProducts);
     }
  },[products, cartItems])

const isItemInCart = (productId)=>{
  return cartItems.some(item=> item.productId === productId)
}



const handleAddToCart = async (productId, newQty) => {
  if (!isLoggedIn) {
    enqueueSnackbar("Login to add an item to the Cart", { variant: "warning" });
    return;
  }

  if (newQty === 1 && isItemInCart(productId)) {
    enqueueSnackbar(
      "Item already in cart. Use the cart sidebar to update quantity or remove item.",
      { variant: "warning" }
    );
    return;
  }

  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${config.endpoint}/cart`, {
      productId,
      qty: newQty
    }, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    setCartitems(response.data);

    // 👇 regenerate complete cart items for Cart component
    const merged = generateCartItemsFrom(response.data, products);
    setCompleteCartitems(merged);

    if (newQty === 0) {
      enqueueSnackbar(`Item removed from cart`, { variant: "success" });
    } else if (newQty > 1) {
      enqueueSnackbar(`Item updated to ${newQty}`, { variant: "success" });
    } else {
      enqueueSnackbar(`Item added to cart`, { variant: "success" });
    }

  } catch (error) {
    console.error("Error updating to cart", error);
    enqueueSnackbar("Failed to update to cart", { variant: "error" });
  }
};
  const displayProducts = inputVal ? searchResult : products;




return (
  <div>
    <Header 
      hasHiddenAuthButtons={false}
      onSearch={debounceSearch}
      searchValue={inputVal}
    />

    <TextField
      className="search-mobile"
      size="small"
      fullWidth
      value={inputVal}
      onChange={debounceSearch}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Search color="primary" />
          </InputAdornment>
        ),
      }}
      placeholder="Search for items/categories"
      name="search"
    />



<Box>
    <Grid container >
      {/* <div style={{display:"flex", flexDirection:"row"}}> */}
      <Grid item xs={12} md={isLoggedIn ? 8 : 12}>
      <Grid item xs={12}>
        <Box className="hero">
          <Typography variant="h5" component="div" className="hero-heading">
            India's <span className="hero-highlight">FASTEST DELIVERY</span> to your door step

          </Typography>
        </Box>
      </Grid>
      {isLoading ? (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading Products...</Typography>
      </Box>
    ) : error ? (
      <Box textAlign="center" my={4}>
        <SentimentDissatisfied fontSize="large" />
        <Typography variant="h6" color="error">No products found</Typography>
      </Box>
    ) : (
      <Grid container spacing={2} sx={{ px: 2, py: 2 }}>
        {displayProducts.map((item) => (
          <Grid item key={item._id} xs={12} sm={6} md={4}>
            <ProductCard 
              key={item._id}
              product={item}
              handleAddToCart={handleAddToCart}
              isItemInCart={isItemInCart}
            />
          </Grid>
        ))}
        
      </Grid>
    )}


    

</Grid>
{isLoggedIn && (

<Grid xs={12} md={4} style={{background:"#E9F5E1"}}>
<Cart
products={products}
items={completeCartitems}
handleQuantity={handleQuantity}

  />
 </Grid>

 )}
    {/* </div> */}
    </Grid>
  

</Box>


    <Footer />
  </div>
);
};

export default Products;
